import { config } from '@/apollo/config';
import CustomRadioButton from '@/components/ui/radio-button';
import { ExternalPartnerNameEnum, UserOrdersQuery } from '@/graphql';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import { Modal, SelectPicker } from 'rsuite';
import { io } from 'socket.io-client';
import styles from './styles.module.scss';

type Props = {
  data: UserOrdersQuery | undefined;
  setInstallmentName: (e: ExternalPartnerNameEnum | string) => void;
  setApplicationId: (e: string | null) => void;
  setInstallmentPeriod: (e: number | null) => void;
  applicationId: string | null;
};

type MonthOption = {
  label: string;
  value: number;
};

type Bank = {
  title: string;
  id: number;
  mouth: MonthOption[];
  price: { month: number; price: number }[];
  totalPrice: number;
};

const CustomArrow = () => {
  return <BsChevronDown size={20} color="#333" />;
};

export default function Credit(props: Props) {
  const {
    data,
    setInstallmentName,
    setApplicationId,
    applicationId,
    setInstallmentPeriod,
  } = props;
  const [socket, setSocket] = useState<any>(null);
  const installmentOptions = useMemo(() => {
    return data?.userDraftOrders?.[0]?.installmentsWithoutIntegration || [];
  }, [data]);
  const [selectedMonths, setSelectedMonths] = useState<{
    [key: string]: number | undefined;
  }>({});
  const [selectedPartner, setSelectedPartner] = useState<string | null>(
    installmentOptions[0]?.partner?.id || null
  );
  const [showIframe, setShowIframe] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(
    null
  );

  const handleMonthChange = (monthAmount: number | null, partnerId: string) => {
    setSelectedMonths(prevState => {
      const newState = { ...prevState };
      if (monthAmount === undefined || monthAmount === null) {
        delete newState[partnerId];
      } else {
        newState[partnerId] = monthAmount;
      }
      return newState;
    });

    if (partnerId === selectedPartner) {
      setInstallmentPeriod(monthAmount ?? null);
    }
  };

  const calculatePrice = (
    totalPrice: number | undefined,
    monthsAmount: number | null | undefined
  ) => {
    if (!totalPrice || !monthsAmount) return '0';
    const pricePerMonth = totalPrice / monthsAmount;
    return pricePerMonth.toLocaleString(undefined, {
      minimumFractionDigits: 0,
    });
  };

  const requestCameraPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission(true);
    } catch (error: any) {
      console.error('Error requesting camera permission:', error);
      setCameraPermission(false);
    }
  }, []);

  useEffect(() => {
    let shouldShowIframe = false;
    if (selectedPartner) {
      const selectedOption = installmentOptions.find(
        option => option?.partner?.id === selectedPartner
      );
      if (selectedOption?.partner?.externalPartnerName) {
        setInstallmentName(selectedOption.partner.externalPartnerName);
        if (
          selectedOption.partner.externalPartnerName ===
          ExternalPartnerNameEnum.ALLGOOD
        ) {
          shouldShowIframe = true;
        }
      } else {
        setInstallmentName(
          selectedOption?.partner?.name?.toLocaleUpperCase() ?? 'RADIUS'
        );
      }
      const monthsData =
        selectedOption?.months?.map(month => ({
          value: month?.month,
        })) || [];

      let periodToSet: number | null = null;
      if (monthsData.length > 0) {
        const defaultMonth = monthsData[0]?.value;
        const currentMonthForSelectedPartner =
          selectedMonths[selectedPartner] ?? defaultMonth;
        periodToSet = currentMonthForSelectedPartner ?? null;
      }
      setInstallmentPeriod(periodToSet);
    } else {
      setInstallmentName('');
      setInstallmentPeriod(null);
    }
    setShowIframe(shouldShowIframe);
    setShowModal(shouldShowIframe);

    if (shouldShowIframe) {
      requestCameraPermission();
    } else {
      setCameraPermission(null);
    }
  }, [selectedPartner, installmentOptions, setInstallmentName]);

  useEffect(() => {
    if (selectedPartner) {
      const selectedOption = installmentOptions.find(
        option => option?.partner?.id === selectedPartner
      );
      if (
        selectedOption?.partner.externalPartnerName ===
        ExternalPartnerNameEnum.ALLGOOD
      ) {
        const iframeRef = document.getElementById(
          'installmentIframe'
        ) as HTMLIFrameElement;
        if (!iframeRef) {
          console.error("Iframe with id 'installmentIframe' not found.");
          return;
        }

        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== 'https://id.allgoodnasiya.uz') {
            console.warn('Received message from unknown origin:', event.origin);
            return;
          }

          if (event.data === 'requestProps') {
            iframeRef.contentWindow?.postMessage(
              {
                props: {
                  public_key: 'b5c2c4ae-47ef-4575-9ce9-3fda32e53a85',
                  broker: true,
                },
              },
              'https://id.allgoodnasiya.uz'
            );
          } else if (
            event.data &&
            typeof event.data === 'object' &&
            event.data !== null &&
            'application_id' in event.data
          ) {
            const applicationId = (event.data as { application_id: string })
              .application_id;
            setApplicationId(applicationId);
          } else if (
            event.data &&
            typeof event.data === 'object' &&
            event.data !== null &&
            'error' in event.data
          ) {
            const errorMessage = (event.data as { error: string }).error;
            console.error('Error from iframe:', errorMessage);
            alert(`Ошибка от AllGood: ${errorMessage}`);
          }
        };

        iframeRef.addEventListener('load', () => {
          window.addEventListener('message', handleMessage);
        });

        window.addEventListener('unload', () => {
          window.removeEventListener('message', handleMessage);
        });

        return () => {
          iframeRef.removeEventListener('load', () => {
            window.removeEventListener('message', handleMessage);
          });
          window.removeEventListener('unload', () => {});
          window.removeEventListener('message', handleMessage);
        };
      }
    }
  }, [showIframe]);

  useEffect(() => {
    if (!applicationId) return;

    const socket = io('https://api.allgoodnasiya.uz');

    setSocket(socket);

    socket.on('connect', () => {
      socket.emit('subscribeToApplication', applicationId);
    });

    socket.on('update', data => {
      console.log('Received update:', data);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from Socket.IO');
    });

    socket.on('connect_error', error => {
      console.error('Socket.IO connection error:', error);
    });

    return () => {
      socket.off('connect');
      socket.off('update');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('error');

      socket.disconnect();
    };
  }, [applicationId]);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowIframe(false);
  };

  useEffect(() => {
    const partnerNameFromServer =
      data?.userDraftOrders?.[0]?.installmentPartnerName;
    const periodFromServer = data?.userDraftOrders?.[0]?.installmentPeriod;

    if (
      partnerNameFromServer &&
      installmentOptions &&
      installmentOptions.length > 0
    ) {
      const targetPartnerName = partnerNameFromServer;
      const selectedOption = installmentOptions.find(option => {
        const currentOptionName = option?.partner?.externalPartnerName;
        const isMatch = currentOptionName === targetPartnerName;
        return isMatch;
      });
      if (selectedOption && selectedOption.partner?.id) {
        const partnerId = selectedOption.partner.id;
        setSelectedPartner(partnerId);
        setInstallmentName(partnerNameFromServer);

        if (typeof periodFromServer === 'number') {
          setSelectedMonths(prevMonths => {
            if (prevMonths[partnerId] !== periodFromServer) {
              return {
                ...prevMonths,
                [partnerId]: periodFromServer,
              };
            }
            return prevMonths;
          });
          setInstallmentPeriod(periodFromServer);
        }
      }
    }
  }, [data]);
  const t = useTranslations('Components.installments');

  return (
    <div className={styles.installments}>
      <h2 className={styles.title}>{t('installment')}</h2>
      <div className={styles.headers}>
        <p className={styles.headersText}>{t('partners')}</p>
        <p className={styles.headersText}>{t('period')}</p>
        <p className={styles.headersText}>{t('per-month')}</p>
        <p className={styles.headersText}>{t('total')}</p>
      </div>

      <div className={styles.selectBanks}>
        {installmentOptions.map((option, index) => {
          if (!option) {
            return null;
          }
          const { partner, months } = option;
          const partnerId = partner?.id;

          const monthsData =
            months?.map(month => ({
              label: `${month?.month} ${t('month')}`,
              value: month?.month,
              totalPrice: month?.price?.amount,
            })) || [];

          if (monthsData.length === 0) {
            return null;
          }

          const defaultMonth = monthsData[0]?.value;
          const selectedMonth = selectedMonths[partnerId] || defaultMonth;

          const selectedMonthData = monthsData.find(
            month => month.value === selectedMonth
          );

          const totalPrice = selectedMonthData?.totalPrice;
          const perMonth = calculatePrice(totalPrice, selectedMonth);

          return (
            <div
              key={option.partner.id}
              className={`${styles.bankCard} ${
                selectedPartner === option.partner.id
                  ? styles.bankCardActive
                  : styles.bankCard
              }`}
            >
              <div className={styles.checkBox}>
                <CustomRadioButton
                  label={partner?.logo ? '' : partner?.name}
                  checked={selectedPartner === partnerId}
                  onChange={() => setSelectedPartner(partnerId)}
                  value={partnerId}
                />
                {partner?.logo && (
                  <Image
                    src={`${config.publicApiUrl}${partner.logo}`}
                    alt={partner?.name}
                    width={99}
                    height={32}
                    objectFit="contain"
                  />
                )}
              </div>

              <div className={styles.selectBox}>
                <p className={styles.wrapperText}>Срок</p>
                <div className={styles.select}>
                  <SelectPicker
                    data={monthsData}
                    caretAs={CustomArrow}
                    appearance="subtle"
                    defaultValue={defaultMonth}
                    cleanable={false}
                    searchable={false}
                    onChange={value => handleMonthChange(value, partnerId)}
                    value={selectedMonth}
                    // style={{ width: 150 }}
                  />
                </div>
              </div>

              <div className={styles.wrapper}>
                <p className={styles.wrapperText}>{t('per-month')}</p>
                <p className={styles.price}>
                  {perMonth} {t('price-month')}
                </p>
              </div>
              <div className={styles.wrapper}>
                <p className={styles.wrapperText}>{t('total')}</p>
                <p className={styles.totalPrice}>
                  {totalPrice?.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                  })}{' '}
                  {t('price')}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={showModal}
        onClose={handleCloseModal}
        size="sm"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Modal.Body
          className={styles.modalBody}
          onClick={(e: any) => e.stopPropagation()}
        >
          <div className={styles.iframeWrapper}>
            <iframe
              id="installmentIframe"
              src="https://id.allgoodnasiya.uz/"
              title="Embedded Application"
              allow="camera; fullscreen"
              width="100%"
              height="500px"
              frameBorder="0"
            ></iframe>
          </div>
        </Modal.Body>
        <Modal.Footer>{/* You can add buttons here if needed */}</Modal.Footer>
      </Modal>
    </div>
  );
}
